import type { CreateRoomResponse, JoinRoomResponse, RoomPublicState } from '@webfile/protocol'

export class SignalingError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'SignalingError'
    this.status = status
    this.code = code
  }
}

export interface SignalingClientOptions {
  /** API origin, empty = same origin */
  baseUrl?: string
  fetchImpl?: typeof fetch
}

/** Default poll interval — keep under server GET rate limit (~180–240/min). */
export const DEFAULT_POLL_INTERVAL_MS = 700

function apiBase(baseUrl?: string): string {
  if (baseUrl != null && baseUrl !== '') return baseUrl.replace(/\/$/, '')
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE) {
    return String(import.meta.env.VITE_API_BASE).replace(/\/$/, '')
  }
  return ''
}

async function parseResponse<T>(res: Response): Promise<T> {
  const text = await res.text()
  let body: unknown = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = { error: 'invalid_response', message: text.slice(0, 200) }
    }
  }
  if (!res.ok) {
    const err = (body || {}) as { error?: string; message?: string }
    throw new SignalingError(
      res.status,
      err.error || 'http_error',
      err.message || res.statusText || 'Request failed',
    )
  }
  return body as T
}

export class SignalingClient {
  private readonly base: string
  private readonly fetchImpl: typeof fetch

  constructor(opts: SignalingClientOptions = {}) {
    this.base = apiBase(opts.baseUrl)
    this.fetchImpl = opts.fetchImpl || fetch.bind(globalThis)
  }

  private url(path: string): string {
    return `${this.base}${path}`
  }

  async createRoom(): Promise<CreateRoomResponse> {
    const res = await this.fetchImpl(this.url('/api/rooms'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: '{}',
    })
    return parseResponse(res)
  }

  async getRoom(code: string): Promise<RoomPublicState> {
    const res = await this.fetchImpl(this.url(`/api/rooms/${encodeURIComponent(code)}`), {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
    return parseResponse(res)
  }

  async joinRoom(code: string, guestId?: string): Promise<JoinRoomResponse> {
    const body: Record<string, string> = { action: 'join' }
    if (guestId) body.guestId = guestId
    const res = await this.fetchImpl(this.url(`/api/rooms/${encodeURIComponent(code)}`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
    return parseResponse(res)
  }

  /** Bump the room's fileRev so peers refetch the manifest. */
  async notifyFile(code: string, clientId: string): Promise<RoomPublicState> {
    const res = await this.fetchImpl(
      this.url(`/api/rooms/${encodeURIComponent(code)}/notify`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Client-Id': clientId },
        body: '{}',
      },
    )
    return parseResponse(res)
  }

  /**
   * Poll room until predicate matches or timeout.
   */
  async pollRoom(
    code: string,
    predicate: (state: RoomPublicState) => boolean,
    opts: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal } = {},
  ): Promise<RoomPublicState> {
    const intervalMs = opts.intervalMs ?? DEFAULT_POLL_INTERVAL_MS
    const timeoutMs = opts.timeoutMs ?? 90_000
    const start = Date.now()

    while (true) {
      if (opts.signal?.aborted) {
        throw new SignalingError(499, 'aborted', 'Polling aborted')
      }
      const state = await this.getRoom(code)
      if (predicate(state)) return state
      if (Date.now() - start > timeoutMs) {
        throw new SignalingError(408, 'poll_timeout', 'Timed out waiting for peer')
      }
      await sleep(intervalMs, opts.signal)
    }
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new SignalingError(499, 'aborted', 'Aborted'))
      return
    }
    const t = setTimeout(resolve, ms)
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(t)
        reject(new SignalingError(499, 'aborted', 'Aborted'))
      },
      { once: true },
    )
  })
}